/**
 * CSV Export Service
 * 
 * Provides functionality to export order and store data to CSV format.
 * Supports both individual and bulk exports.
 * 
 * @module CSVExportService
 */

import { Order } from '../@types/order';
import { Store } from '../@types/store';
import { formatOrderDateForDisplay } from '../utils/calendarDate';
import { collection, getDocs, getDocsFromServer, query, orderBy, limit } from 'firebase/firestore';
import { db } from '../config/firebase';
import { StoreService } from './StoreService';

export interface CSVExportOptions {
  /** Include order items in export */
  includeItems?: boolean;
  /** Include customer information */
  includeCustomerInfo?: boolean;
  /** Custom filename */
  filename?: string;
}

export class CSVExportService {
  /**
   * Get all items from an order (combines all item categories including supplies)
   */
  private static getAllOrderItems(order: Order) {
    const allItems = [
      ...(order.heritageItems || []),
      ...(order.livingstonItems || []),
      ...(order.flowerItems || []),
      ...(order.vegetableItems || []),
      ...((order as any).supplyItems || [])
    ];
    return allItems;
  }
  /**
   * Export a single order to CSV
   */
  static async exportOrderToCSV(order: Order, options: CSVExportOptions = {}): Promise<void> {
    const csvData = this.formatOrderForCSV(order, options);
    const filename = options.filename || `order_${order.orderId}_${new Date().toISOString().split('T')[0]}.csv`;
    
    this.downloadCSV(csvData, filename);
  }

  /**
   * Export multiple orders to CSV
   */
  static async exportOrdersToCSV(orders: Order[], options: CSVExportOptions = {}): Promise<void> {
    if (orders.length === 0) {
      throw new Error('No orders to export');
    }

    const csvData = this.formatOrdersForCSV(orders, options);
    const filename = options.filename || `orders_export_${new Date().toISOString().split('T')[0]}.csv`;
    
    this.downloadCSV(csvData, filename);
  }

  /**
   * Format a single order for CSV export
   * Each field gets its own column for proper spreadsheet formatting
   */
  private static formatOrderForCSV(order: Order, options: CSVExportOptions): string {
    // Build headers - always include item columns if items are requested
    const baseHeaders = [
      'Order ID',
      'Customer #',
      'Group',
      'Salesperson #',
      'Delivery Type',
      'Order Date M/D/Y',
      'Delivery Date M/D/Y',
      'Name',
      'PO NUMBER',
      'Address',
      'City',
      'Province',
      'Postal Code',
      'Email',
      'Phone',
      'Special Instructions'
    ];

    // Add item columns if items should be included
    const itemHeaders = options.includeItems ? [
      'Item Code',
      'Item Description',
      'Item Category',
      'Quantity',
      'Unit Price',
      'Total Price'
    ] : [];

    const headers = [...baseHeaders, ...itemHeaders];

    const allItems = options.includeItems ? this.getAllOrderItems(order) : [];
    const rows: string[][] = [];

    // If no items or items not included, create one row
    if (allItems.length === 0 || !options.includeItems) {
      rows.push([
        order.orderId || 'N/A',
        order.customerNumber || order.storeId || 'N/A',
        order.salesRepName || (order as any).salesGroup || 'N/A',
        order.salesRepId || order.salespersonNo || 'N/A',
        order.orderType === 'custOrder' ? 'Customer Order' : 
          order.orderType === 'offCarDelivery' ? 'Off Car Delivery' : 
          'Standard Order',
        this.formatDate(order.orderDate),
        this.formatDate(order.deliveryDate),
        order.storeName || 'N/A',
        order.poNumber || 'N/A',
        order.address || 'N/A',
        order.city || 'N/A',
        order.province || (order as any).state || 'N/A',
        order.postalCode || (order as any).pc || 'N/A',
        order.email || (order as any).customerEmail || 'N/A',
        order.phoneNumber || (order as any).phone || 'N/A',
        order.specialInstructions || 'N/A',
        // Empty item columns if items not included
        ...(options.includeItems ? ['', '', '', '', '', ''] : [])
      ]);
    } else {
      // Create one row per item, repeating order information
      allItems.forEach(item => {
        // Get item code - handle multiple field name variations
        const itemCode = item.code || 
                        (item as any).ItemCode || 
                        (item as any).itemCode || 
                        (item as any).item_code ||
                        (item as any).id || 
                        'N/A';
        const itemCategory = item.category || item.categoryName || 'N/A';
        
        rows.push([
          order.orderId || 'N/A',
          order.customerNumber || order.storeId || 'N/A',
          order.salesRepName || (order as any).salesGroup || 'N/A',
          order.salesRepId || order.salespersonNo || 'N/A',
          order.orderType === 'custOrder' ? 'Customer Order' : 
            order.orderType === 'offCarDelivery' ? 'Off Car Delivery' : 
            'Standard Order',
          this.formatDate(order.orderDate),
          this.formatDate(order.deliveryDate),
          order.storeName || 'N/A',
          order.poNumber || 'N/A',
          order.address || 'N/A',
          order.city || 'N/A',
          order.province || (order as any).state || 'N/A',
          order.postalCode || (order as any).pc || 'N/A',
          order.email || (order as any).customerEmail || 'N/A',
          order.phoneNumber || (order as any).phone || 'N/A',
          order.specialInstructions || 'N/A',
          // Item columns
          itemCode,
          item.packetDescription || 'N/A',
          itemCategory,
          item.quantity?.toString() || '0',
          item.price?.toFixed(2) || '0.00',
          ((item.quantity || 0) * (item.price || 0)).toFixed(2)
        ]);
      });
    }

    return this.arrayToCSV([headers, ...rows]);
  }

  /**
   * Format multiple orders for CSV export
   * Each field gets its own column for proper spreadsheet formatting
   */
  private static formatOrdersForCSV(orders: Order[], options: CSVExportOptions): string {
    // Build headers - always include item columns if items are requested
    const baseHeaders = [
      'Order ID',
      'Customer #',
      'Group',
      'Salesperson #',
      'Delivery Type',
      'Order Date M/D/Y',
      'Delivery Date M/D/Y',
      'Name',
      'PO NUMBER',
      'Address',
      'City',
      'Province',
      'Postal Code',
      'Email',
      'Phone',
      'Special Instructions'
    ];

    // Add item columns if items should be included
    const itemHeaders = options.includeItems ? [
      'Item Code',
      'Item Description',
      'Item Category',
      'Quantity',
      'Unit Price',
      'Total Price'
    ] : [];

    const headers = [...baseHeaders, ...itemHeaders];

    const rows: string[][] = [];

    orders.forEach(order => {
      const allItems = options.includeItems ? this.getAllOrderItems(order) : [];
      
      // If no items or items not included, create one row per order
      if (allItems.length === 0 || !options.includeItems) {
        rows.push([
          order.orderId || 'N/A',
          order.customerNumber || order.storeId || 'N/A',
          order.salesRepName || (order as any).salesGroup || 'N/A',
          order.salesRepId || order.salespersonNo || 'N/A',
          order.orderType === 'custOrder' ? 'Customer Order' : 
            order.orderType === 'offCarDelivery' ? 'Off Car Delivery' : 
            'Standard Order',
          this.formatDate(order.orderDate),
          this.formatDate(order.deliveryDate),
          order.storeName || 'N/A',
          order.poNumber || 'N/A',
          order.address || 'N/A',
          order.city || 'N/A',
          order.province || (order as any).state || 'N/A',
          order.postalCode || (order as any).pc || 'N/A',
          order.email || (order as any).customerEmail || 'N/A',
          order.phoneNumber || (order as any).phone || 'N/A',
          order.specialInstructions || 'N/A',
          // Empty item columns if items not included
          ...(options.includeItems ? ['', '', '', '', '', ''] : [])
        ]);
      } else {
        // Create one row per item, repeating order information
        allItems.forEach(item => {
          // Get item code - handle multiple field name variations
          const itemCode = item.code || 
                          (item as any).ItemCode || 
                          (item as any).itemCode || 
                          (item as any).item_code ||
                          (item as any).id || 
                          'N/A';
          const itemCategory = item.category || item.categoryName || 'N/A';
          
          rows.push([
            order.orderId || 'N/A',
            order.customerNumber || order.storeId || 'N/A',
            order.salesRepName || (order as any).salesGroup || 'N/A',
            order.salesRepId || order.salespersonNo || 'N/A',
            order.orderType === 'custOrder' ? 'Customer Order' : 
              order.orderType === 'offCarDelivery' ? 'Off Car Delivery' : 
              'Standard Order',
            this.formatDate(order.orderDate),
            this.formatDate(order.deliveryDate),
            order.storeName || 'N/A',
            order.poNumber || 'N/A',
            order.address || 'N/A',
            order.city || 'N/A',
            order.province || (order as any).state || 'N/A',
            order.postalCode || (order as any).pc || 'N/A',
            order.email || (order as any).customerEmail || 'N/A',
            order.phoneNumber || (order as any).phone || 'N/A',
            order.specialInstructions || 'N/A',
            // Item columns
            itemCode,
            item.packetDescription || 'N/A',
            itemCategory,
            item.quantity?.toString() || '0',
            item.price?.toFixed(2) || '0.00',
            ((item.quantity || 0) * (item.price || 0)).toFixed(2)
          ]);
        });
      }
    });

    return this.arrayToCSV([headers, ...rows]);
  }

  /**
   * Convert array to CSV string
   */
  private static arrayToCSV(data: string[][]): string {
    return data.map(row => 
      row.map(cell => {
        // Escape quotes and wrap in quotes if contains comma, quote, or newline
        const escaped = cell.replace(/"/g, '""');
        if (escaped.includes(',') || escaped.includes('"') || escaped.includes('\n')) {
          return `"${escaped}"`;
        }
        return escaped;
      }).join(',')
    ).join('\n');
  }

  /**
   * Format date for CSV
   */
  private static formatDate(date: Date | string | undefined): string {
    if (!date) return 'N/A';
    if (typeof date === 'string') {
      return formatOrderDateForDisplay(date, 'en-US', {
        year: 'numeric',
        month: 'numeric',
        day: 'numeric'
      });
    }
    return date.toLocaleDateString('en-US');
  }

  /**
   * Export generic data array to CSV
   * Useful for custom data formats
   */
  static async exportDataToCSV(
    data: Record<string, any>[],
    options: { filename?: string } = {}
  ): Promise<void> {
    if (data.length === 0) {
      throw new Error('No data to export');
    }

    // Extract headers from first object
    const headers = Object.keys(data[0]);
    
    // Convert data to array format
    const rows = data.map(item => headers.map(header => {
      const value = item[header];
      return value !== null && value !== undefined ? String(value) : '';
    }));

    const csvContent = this.arrayToCSV([headers, ...rows]);
    const filename = options.filename || `export_${new Date().toISOString().split('T')[0]}.csv`;
    
    this.downloadCSV(csvContent, filename);
  }

  /**
   * Download CSV file
   */
  private static downloadCSV(csvContent: string, filename: string): void {
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    
    if (link.download !== undefined) {
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', filename);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    }
  }

  /**
   * Generate order CSV as Blob (for email attachments)
   * Returns a Blob instead of downloading the file
   */
  static generateOrderCSVBlob(order: Order, options: CSVExportOptions = {}): Blob {
    const csvData = this.formatOrderForCSV(order, { ...options, includeItems: true });
    return new Blob([csvData], { type: 'text/csv;charset=utf-8;' });
  }

  /**
   * Generate multiple orders CSV as Blob (for email attachments)
   * Returns a Blob instead of downloading the file
   */
  static generateOrdersCSVBlob(orders: Order[], options: CSVExportOptions = {}): Blob {
    const csvData = this.formatOrdersForCSV(orders, { ...options, includeItems: true });
    return new Blob([csvData], { type: 'text/csv;charset=utf-8;' });
  }

  /**
   * Fetch ALL stores from Firestore for a full-database export.
   * Excludes archived stores. Calls onProgress with (loaded, total) as batches complete.
   */
  static async fetchAllStoresForExport(
    onProgress?: (loaded: number, total: number) => void
  ): Promise<Store[]> {
    const storesRef = collection(db, 'Stores');
    const snapshot = await getDocsFromServer(storesRef);

    const docs = snapshot.docs.filter(d => {
      const data = d.data();
      const status = data.status ?? data.Status;
      return status !== 'archived';
    });

    const total = docs.length;
    const stores: Store[] = [];
    const batchSize = 50;

    for (let i = 0; i < docs.length; i += batchSize) {
      const batch = docs.slice(i, i + batchSize);
      batch.forEach(d => stores.push(StoreService.mapDbDataToStore(d.id, d.data())));
      onProgress?.(Math.min(i + batchSize, total), total);
    }

    return stores;
  }

  /**
   * Get the most recent visit details for a store
   */
  private static async getLastVisitDetails(storeId: string): Promise<{
    visitType?: string;
    storeLevel?: number;
    lowStockCategories?: string[];
    competitors?: string[];
    competitorData?: Array<{ name: string; stockLevel?: number; displayPanels?: number }>;
    requestManagerContact?: boolean;
  }> {
    try {
      const notesRef = collection(db, 'Stores', storeId, 'notes');
      const notesQuery = query(notesRef, orderBy('createdAt', 'desc'), limit(1));
      const notesSnapshot = await getDocs(notesQuery);
      
      if (!notesSnapshot.empty) {
        const latestNote = notesSnapshot.docs[0].data();
        return {
          visitType: latestNote.visitType || '',
          storeLevel: latestNote.storeLevel !== undefined ? Number(latestNote.storeLevel) : undefined,
          lowStockCategories: latestNote.lowStockCategories || [],
          competitors: latestNote.competitors || [],
          competitorData: latestNote.competitorData || [],
          requestManagerContact: latestNote.requestManagerContact || false,
        };
      }
    } catch (error) {
      console.warn(`Failed to get last visit details for store ${storeId}:`, error);
    }
    
    return {};
  }

  /**
   * Export stores to CSV with comprehensive visit data
   */
  static async exportStoresToCSV(
    stores: Store[],
    options: { filename?: string; includeNotes?: boolean } = {}
  ): Promise<void> {
    if (stores.length === 0) {
      throw new Error('No stores to export');
    }

    const headers = [
      'Customer #',
      'Store Name',
      'Address',
      'City',
      'Province',
      'Postal Code',
      'Phone',
      'Email',
      'Sales Group',
      'Salesperson #',
      'Sales Person Name',
      'Region',
      'Territory',
      'Status',
      'Total Visits',
      'Last Visit Date',
      'Last Visit Type',
      'Stock Level (%)',
      'Low Stock Categories',
      'Competitors Spotted',
      'Competitor Stock Levels',
      'Competitor Number of Panels',
      'Manager Contact Requested'
    ];

    // Fetch visit details for all stores in parallel
    const visitDetailsPromises = stores.map(store => this.getLastVisitDetails(store.id));
    const visitDetails = await Promise.all(visitDetailsPromises);

    const rows = stores.map((store, index) => {
      const visitDetail = visitDetails[index];
      
      // Format last visit date
      const lastVisitDate = store.lastNoteDate || store.lastVisit;
      const lastVisitStr = lastVisitDate 
        ? (lastVisitDate instanceof Date 
            ? lastVisitDate.toLocaleDateString('en-US')
            : (lastVisitDate.toDate ? lastVisitDate.toDate().toLocaleDateString('en-US') : 'N/A'))
        : 'Never';

      // Format visit type
      const visitTypeMap: Record<string, string> = {
        'setup': 'Setup Visit',
        'maintenance': 'Maintenance Visit',
        'requested': 'Store/Manager Requested Visit'
      };
      const visitTypeStr = visitDetail.visitType ? (visitTypeMap[visitDetail.visitType] || visitDetail.visitType) : '';

      return [
        store.customerNo || store.CustomerNo || '',
        store.name || store.StoreName || '',
        store.address || store.Address || '',
        store.city || store.City || '',
        store.state || store.province || store.Prov || '',
        (store as any).postalCode || store.PostalCode || store.pc || '',
        store.phone || store.telephone || store.Telephone || '',
        store.email || (store as any).Email || (store as any).EmailAdddress || '',
        store.salesGroup || (store as any).SalesGroup || '',
        store.salespersonNo || (store as any).SalespersonNo || '',
        store.salesPersonName || '',
        store.region || '',
        store.territory || '',
        store.status || 'active',
        String(store.notesCount || 0),
        lastVisitStr,
        visitTypeStr,
        visitDetail.storeLevel !== undefined ? String(visitDetail.storeLevel) : '',
        (visitDetail.lowStockCategories || []).join('; '),
        (visitDetail.competitors || []).join('; '),
        (visitDetail.competitorData || [])
          .filter(c => c.stockLevel !== undefined)
          .map(c => `${c.name}: ${c.stockLevel}%`)
          .join('; '),
        (visitDetail.competitorData || [])
          .filter(c => c.displayPanels !== undefined && c.displayPanels > 0)
          .map(c => `${c.name}: ${c.displayPanels}`)
          .join('; '),
        visitDetail.requestManagerContact ? 'Yes' : 'No'
      ];
    });

    const csvContent = this.arrayToCSV([headers, ...rows]);
    const filename = options.filename || `stores_export_${new Date().toISOString().split('T')[0]}.csv`;
    
    this.downloadCSV(csvContent, filename);
  }

  /**
   * Export stores to XLSX format with comprehensive visit data
   */
  static async exportStoresToXLSX(
    stores: Store[],
    options: { filename?: string } = {}
  ): Promise<void> {
    if (stores.length === 0) {
      throw new Error('No stores to export');
    }

    // For XLSX, we'll use a simple approach with a library or create XML-based xlsx
    // For now, we'll fall back to CSV with .xlsx extension hint
    // A proper implementation would use a library like xlsx or exceljs
    
    const headers = [
      'Customer #',
      'Store Name',
      'Address',
      'City',
      'Province',
      'Postal Code',
      'Phone',
      'Email',
      'Sales Group',
      'Salesperson #',
      'Sales Person Name',
      'Region',
      'Territory',
      'Status',
      'Total Visits',
      'Last Visit Date',
      'Last Visit Type',
      'Stock Level (%)',
      'Low Stock Categories',
      'Competitors Spotted',
      'Competitor Stock Levels',
      'Competitor Number of Panels',
      'Manager Contact Requested'
    ];

    // Fetch visit details for all stores in parallel
    const visitDetailsPromises = stores.map(store => this.getLastVisitDetails(store.id));
    const visitDetails = await Promise.all(visitDetailsPromises);

    const rows = stores.map((store, index) => {
      const visitDetail = visitDetails[index];
      
      // Format last visit date
      const lastVisitDate = store.lastNoteDate || store.lastVisit;
      const lastVisitStr = lastVisitDate 
        ? (lastVisitDate instanceof Date 
            ? lastVisitDate.toLocaleDateString('en-US')
            : (lastVisitDate.toDate ? lastVisitDate.toDate().toLocaleDateString('en-US') : 'N/A'))
        : 'Never';

      // Format visit type
      const visitTypeMap: Record<string, string> = {
        'setup': 'Setup Visit',
        'maintenance': 'Maintenance Visit',
        'requested': 'Store/Manager Requested Visit'
      };
      const visitTypeStr = visitDetail.visitType ? (visitTypeMap[visitDetail.visitType] || visitDetail.visitType) : '';

      return [
        store.customerNo || store.CustomerNo || '',
        store.name || store.StoreName || '',
        store.address || store.Address || '',
        store.city || store.City || '',
        store.state || store.province || store.Prov || '',
        (store as any).postalCode || store.PostalCode || store.pc || '',
        store.phone || store.telephone || store.Telephone || '',
        store.email || (store as any).Email || (store as any).EmailAdddress || '',
        store.salesGroup || (store as any).SalesGroup || '',
        store.salespersonNo || (store as any).SalespersonNo || '',
        store.salesPersonName || '',
        store.region || '',
        store.territory || '',
        store.status || 'active',
        String(store.notesCount || 0),
        lastVisitStr,
        visitTypeStr,
        visitDetail.storeLevel !== undefined ? String(visitDetail.storeLevel) : '',
        (visitDetail.lowStockCategories || []).join('; '),
        (visitDetail.competitors || []).join('; '),
        (visitDetail.competitorData || [])
          .filter(c => c.stockLevel !== undefined)
          .map(c => `${c.name}: ${c.stockLevel}%`)
          .join('; '),
        (visitDetail.competitorData || [])
          .filter(c => c.displayPanels !== undefined && c.displayPanels > 0)
          .map(c => `${c.name}: ${c.displayPanels}`)
          .join('; '),
        visitDetail.requestManagerContact ? 'Yes' : 'No'
      ];
    });

    // Create tab-separated values for better Excel compatibility
    const tsvContent = [headers, ...rows].map(row => 
      row.map(cell => {
        const escaped = String(cell).replace(/\t/g, ' ').replace(/"/g, '""');
        if (escaped.includes('\n') || escaped.includes('"')) {
          return `"${escaped}"`;
        }
        return escaped;
      }).join('\t')
    ).join('\n');

    const filename = options.filename || `stores_export_${new Date().toISOString().split('T')[0]}.xls`;
    
    // Use xls extension for tab-separated that Excel can open
    const blob = new Blob([tsvContent], { type: 'application/vnd.ms-excel;charset=utf-8;' });
    const link = document.createElement('a');
    
    if (link.download !== undefined) {
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', filename);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    }
  }
}
